import { Timestamp } from "@bufbuild/protobuf";
import { addYears, subDays, subYears } from "date-fns";
import {
  type CreateCompanyRequest,
  CreateCompanyResponse,
  type GetAllCitiesRequest,
  GetAllCitiesResponse,
  type GetCategoriesRequest,
  GetCategoriesResponse,
  type GetMyCompaniesRequest,
  GetMyCompaniesResponse,
  type UpdateCompanyRequest,
  UpdateCompanyResponse,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import { CompanyWithStores } from "schema/gen/es/chiikipoint/model/v2/model_pb";

import { encodeReadableId, nextPrefixedId, simulateDelay } from "./utils";

const storesPerCompany = 4;
const createStoreId = (companyIndex: number, storeIndex: number): string => {
  const zeroBasedIndex =
    (companyIndex - 1) * storesPerCompany + (storeIndex - 1);
  return encodeReadableId(zeroBasedIndex, 8);
};

export const mockGetMyCompanies = async (
  _req: GetMyCompaniesRequest,
): Promise<GetMyCompaniesResponse> => {
  await simulateDelay();

  const now = new Date();
  const oneYearAgo = subYears(now, 1);
  const hundredYearsLater = addYears(now, 100);
  const yesterday = subDays(now, 1);
  const futureDate = addYears(now, 1);
  const generateCompanyWithStores = (i: number): CompanyWithStores => {
    return new CompanyWithStores({
      company: {
        id: `67e55044-10b1-426f-9247-bb680e5f${i.toString(16).padStart(4, "0")}`,
        name: `みやポマート${i}`,
        ownerName: "日本橋 太郎",
        address: "宮城県仙台市青葉区一番町１丁目３−２",
        contact: "022-123-4567",
        iconUrl: "/misc/default-store-icon.png",
        bankName: "日本橋銀行",
        bankBranchName: "日本橋",
        bankAccountType: "普通",
        bankAccountNumber: "1234567",
        bankAccountHolderName: "日本橋 太郎",
      },
      stores: [
        {
          id: createStoreId(i, 1),
          name: "みやぎ生協",
          fullAddress: "宮城県仙台市青葉区一番町１丁目３−２",
          city: "宮城県仙台市青葉区",
          location: {
            latitude: 38.25783587179756,
            longitude: 140.87284595831724,
          },
          category: "コンビニ",
          iconUrl: "/misc/default-store-icon.png",
          openedAt: Timestamp.fromDate(oneYearAgo),
        },
        {
          id: createStoreId(i, 2),
          name: "丸森観光物産",
          fullAddress: "宮城県伊具郡丸森町字下滝12",
          city: "宮城県伊具郡丸森町",
          location: {
            latitude: 38.25783587179756,
            longitude: 140.87284595831724,
          },
          category: "コンビニ",
          iconUrl: "/misc/default-store-icon.png",
          openedAt: Timestamp.fromDate(oneYearAgo),
          closedAt: Timestamp.fromDate(yesterday),
        },
        {
          id: createStoreId(i, 3),
          name: "テスト店舗3",
          fullAddress: "宮城県仙台市青葉区一番町１丁目３−２",
          city: "宮城県仙台市青葉区",
          location: {
            latitude: 38.25783587179756,
            longitude: 140.87284595831724,
          },
          category: "コンビニ",
          iconUrl: "/misc/default-store-icon.png",
          openedAt: Timestamp.fromDate(oneYearAgo),
          closedAt: Timestamp.fromDate(futureDate),
        },
        {
          id: createStoreId(i, 4),
          name: "テスト店舗4とても長い店舗名のテストtesting",
          fullAddress: "宮城県仙台市青葉区一番町１丁目３−２",
          city: "宮城県仙台市青葉区",
          location: {
            latitude: 38.25783587179756,
            longitude: 140.87284595831724,
          },
          category: "コンビニ",
          iconUrl: "/misc/default-store-icon.png",
          openedAt: Timestamp.fromDate(hundredYearsLater),
        },
      ],
    });
  };

  const companies = Array.from({ length: 1000 }, (_, i) =>
    generateCompanyWithStores(i + 1),
  );
  return new GetMyCompaniesResponse({
    companyWithStores: companies,
  });
};

export const mockGetAllCities = async (
  _req: GetAllCitiesRequest,
): Promise<GetAllCitiesResponse> => {
  await simulateDelay();
  return new GetAllCitiesResponse({
    cities: [
      "宮城県仙台市青葉区",
      "宮城県仙台市宮城野区",
      "宮城県仙台市若林区",
      "宮城県仙台市太白区",
      "宮城県仙台市泉区",
      "宮城県石巻市",
      "宮城県塩竈市",
      "宮城県気仙沼市",
      "宮城県白石市",
      "宮城県名取市",
      "宮城県多賀城市",
      "宮城県岩沼市",
      "宮城県登米市",
      "宮城県栗原市",
      "宮城県東松島市",
      "宮城県大崎市",
    ],
  });
};

export const mockGetCategories = async (
  _req: GetCategoriesRequest,
): Promise<GetCategoriesResponse> => {
  await simulateDelay();
  return new GetCategoriesResponse({
    categories: [
      "コンビニ",
      "スーパーマーケット",
      "飲食店",
      "カフェ",
      "居酒屋",
      "ホテル",
      "旅館",
      "観光施設",
      "物産店",
      "その他",
    ],
  });
};

export const mockCreateCompany = async (
  _req: CreateCompanyRequest,
): Promise<CreateCompanyResponse> => {
  await simulateDelay();
  return new CreateCompanyResponse({
    companyId: nextPrefixedId(
      "create-company",
      "67e55044-10b1-426f-9247-bb680e5f",
      {
        pad: 4,
        radix: 16,
      },
    ),
  });
};

export const mockUpdateCompany = async (
  req: UpdateCompanyRequest,
): Promise<UpdateCompanyResponse> => {
  await simulateDelay();
  return new UpdateCompanyResponse({
    companyId: req.companyId,
  });
};
